import React, { useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { getThemeForHostname } from './tenants';
import { ThemeModeContext, ColorMode } from './ThemeModeContext';

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ColorMode>(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const urlMode = params.get('theme_mode') || params.get('theme');
      if (urlMode === 'dark' || urlMode === 'light') {
        localStorage.setItem('theme_mode', urlMode);
        return urlMode;
      }
    } catch (e) {
      // ignore
    }
    const saved = localStorage.getItem('theme_mode');
    if (saved === 'dark' || saved === 'light') {
      return saved;
    }
    // Check system preference if no manual setting
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });

  const toggleColorMode = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  useEffect(() => {
    localStorage.setItem('theme_mode', mode);
    document.documentElement.setAttribute('data-theme', mode);
    document.body.setAttribute('data-theme', mode);
  }, [mode]);

  const hostname = window.location.hostname;

  const appTheme = useMemo(() => {
    const themeOptions = getThemeForHostname(hostname, mode);
    return createTheme(themeOptions);
  }, [hostname, mode]);

  const contextValue = useMemo(
    () => ({
      mode,
      toggleColorMode,
      setColorMode: setMode,
    }),
    [mode]
  );

  return (
    <ThemeModeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={appTheme}>
        <StyledThemeProvider theme={appTheme}>
          {children}
        </StyledThemeProvider>
      </MuiThemeProvider>
    </ThemeModeContext.Provider>
  );
};
